/**
 * Custom Alert Template
 */

import { escapeHtml, createBlockWrapper } from '../utils';
import { applyThemeOverrides } from './utils';
import type { TemplateRenderer } from './types';

export const renderCustomAlert: TemplateRenderer = (
  config,
  data,
  container,
  onAction,
  themeOverrides
) => {
  const defaultType = (config.type as string) || 'info';
  const showIcon = (config.showIcon as boolean) ?? true;
  const dismissible = (config.dismissible as boolean) ?? false;

  // Data can have type, title, message, action
  const alertType = (data.type as string) || defaultType;
  const title = data.title as string | undefined;
  const message = (data.message || data.text) as string;
  const action = data.action as { label: string; value: string } | undefined;

  const wrapper = createBlockWrapper('custom-alert');
  wrapper.classList.add(`pms-custom-alert-${alertType}`);
  applyThemeOverrides(wrapper, themeOverrides);

  let html = '';

  // Icon
  if (showIcon) {
    const icons: Record<string, string> = {
      info: 'ℹ️',
      success: '✓',
      warning: '⚠',
      error: '✗',
      promo: '🎉'
    };
    html += `<div class="pms-alert-icon">${icons[alertType] || icons.info}</div>`;
  }

  // Content
  html += '<div class="pms-alert-content">';
  if (title) {
    html += `<div class="pms-alert-title">${escapeHtml(title)}</div>`;
  }
  if (message) {
    html += `<div class="pms-alert-message">${escapeHtml(message)}</div>`;
  }
  html += '</div>';

  // Action button
  if (action) {
    html += `<button class="pms-alert-action" data-action="${escapeHtml(action.value)}">${escapeHtml(action.label)}</button>`;
  }

  // Dismiss button
  if (dismissible) {
    html += '<button class="pms-alert-dismiss">×</button>';
  }

  wrapper.innerHTML = html;

  // Bind action
  const actionBtn = wrapper.querySelector('.pms-alert-action');
  if (actionBtn && onAction) {
    actionBtn.addEventListener('click', () => {
      onAction(`alert:${action?.value}`);
    });
  }

  // Bind dismiss
  const dismissBtn = wrapper.querySelector('.pms-alert-dismiss');
  if (dismissBtn) {
    dismissBtn.addEventListener('click', () => {
      wrapper.remove();
    });
  }

  container.appendChild(wrapper);
};
